import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PlanOption, { shortPlanName } from './PlanOption'
import type { PlanPromo } from './PlanOption'
import PlanCompare from './PlanCompare'
import type { ComparePlan, ComparePlanKey } from './PlanCompare'
import ProDetails from './ProDetails'
import PlanFaq from './PlanFaq'
import CNYPromoSection from './CNYPromoSection'
import './plan-tab.css'
import { useTranslation } from 'react-i18next'
import { formatDate, formatList, formatPercent } from '../../../i18n/format'
import { AppUseSelector } from '../../../redux/hooks'
import PaymentSupportModal from 'containers/modal/payment-support-modal'
import {
  downloadInvoice,
  getStripePlan,
  stripeUpdatePaymentMethodLink,
  stripeCheckoutLink,
  downGradePlan,
  churnResume,
  getProfile,
  validateCouponCode,
} from '../../../redux/actions/myAccountAction'
import ChurnFlowModal from './churn-flow/ChurnFlowModal'
import { captureChurnEvent } from '../../../utils/posthogChurn'
import { openSupportChat } from '../../../utils/crisp'
import { requestStudentDiscount } from '../../../utils/studentDiscount'
import { RegionalPricingContext, formatMoney, getPlanAmount } from 'utils/currency'
import RegionalPricingNotice from '../../../containers/regional-pricing/RegionalPricingNotice'
import { membershipNameType } from '../../../utils/constant'
import { captureWeeklyEvent } from '../../../utils/posthogWeekly'
import {
  APRIL_STANDARD_PROMO,
  getAprilStandardPromoTimeLeft,
  isAprilStandardPromoActive,
  shouldApplyAprilStandardPromoToPlan,
} from '../../../utils/aprilStandardPromo'
import { isUserEligibleForCNYPromo } from '../../../utils/cnyPromo'
import BackToSchoolInvite from './BackToSchoolInvite'
import InviteFriendsCard from '../../referral/InviteFriendsCard'
import {
  BACK_TO_SCHOOL_2026,
  getBackToSchool2026Pricing,
  isUserEligibleForBackToSchool2026,
  shouldApplyBackToSchool2026ToPlan,
} from '../../../utils/backToSchool2026'
import {
  DRAGON_BOAT_PROMO,
  getDragonBoatPromoPricing,
  getDragonBoatPromoTimeLeft,
  isDragonBoatPromoActive,
  shouldApplyDragonBoatPromoToPro,
} from '../../../utils/dragonBoatPromo'
import {
  WIN_BACK_PROMO,
  getWinBackPromoPricing,
  getWinBackPromoTimeLeft,
  isWinBackPromoActive,
  shouldApplyWinBackPromoToPlan,
} from '../../../utils/winBackPromo'
import { CouponDetails } from '../../../types/stripeTypes'
import OfferCountdown from 'components/OfferCountdown'
import OfferJourney from 'components/OfferJourney'
import { useTrialDays } from '../../referral/useTrialDays'

const billingPlan = [
  { labelKey: 'plan:billing.monthly', value: 'monthly' },
  { labelKey: 'plan:billing.yearly', value: 'yearly' },
] as const

// Who each plan is for, in one line. The table under the cards spells out the rest.
const TAGLINE_KEYS: Record<string, string> = {
  [membershipNameType.Pro]: 'plan:taglines.pro',
  [membershipNameType.Standard]: 'plan:taglines.standard',
  [membershipNameType.Weekly]: 'plan:taglines.weekly',
}

const PlanTab = () => {
  const { t } = useTranslation('plan')
  const [billing, setBilling] = useState<'monthly' | 'yearly'>('yearly')
  const [stripePlan, setStripePlan] = useState<any>([])
  const [regionalPricing, setRegionalPricing] = useState<RegionalPricingContext | null>(null)
  const [openPaymentSupportModal, setOpenPaymentSupportModal] = useState(false)
  const [churnFlowOpen, setChurnFlowOpen] = useState(false)
  const [isResuming, setIsResuming] = useState(false)
  const [showCouponInput, setShowCouponInput] = useState(false)
  const [couponCode, setCouponCode] = useState('')
  const [couponDetails, setCouponDetails] = useState<CouponDetails | null>(null)
  const [couponError, setCouponError] = useState('')
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false)
  // Which plan's checkout is opening, so the page answers the click at once and every other
  // button waits; and the plan whose checkout failed, so the error shows under its own button.
  const [pendingPriceId, setPendingPriceId] = useState<string | null>(null)
  const [checkoutError, setCheckoutError] = useState<{ priceId: string; message: string } | null>(null)
  // 5 days, or 14 once the server confirms a referred friend: the same length checkout grants.
  const trialDays = useTrialDays()
  const { membership, paymentProfile, stripe, credentials, trialEligibility, backToSchool2026 } = AppUseSelector(
    ({ user }) => user,
  )
  const isAprilPromoActive = isAprilStandardPromoActive()
  const isDragonBoatActive = isDragonBoatPromoActive()
  const isWinBackActive = isWinBackPromoActive()
  // Free learners the backend flagged as eligible get the free trial on Standard and on Pro.
  // The server decides with the same rule checkout applies (dev-backend
  // src/common/trial-eligibility), so the page never offers a trial checkout would refuse.
  const isTrialEligible =
    membership?.name === membershipNameType.Free && Boolean(trialEligibility?.isEligible)
  const showCNYPromo = !isAprilPromoActive && isUserEligibleForCNYPromo(membership?.name)
  // Back-to-School 2026: an invite-only campaign, so eligibility comes from the server's seeded
  // allow-list rather than being derived here. Re-checked at checkout — this only drives display.
  const isBackToSchoolEligible = isUserEligibleForBackToSchool2026({
    isAllowListed: backToSchool2026?.isEligible,
    membershipName: membership?.name,
  })
  // Every one of these discounts monthly billing only, so any of them forces the monthly
  // view. They are scheduled not to overlap; if they ever did, the else-if chain below
  // picks one.
  const isMonthlyPromoActive = isDragonBoatActive || isWinBackActive || isBackToSchoolEligible
  const validDate = formatDate(Number(paymentProfile?.expirationUnix) * 1000, 'medium')

  // Re-fetch when memberId becomes available. credentials.memberId is not persisted across full
  // page loads (the user slice only persists `level`), so after a redirect — e.g. Stripe ->
  // /payment-failed -> "Back to Settings", a hard refresh, or a direct link — it starts empty and
  // is repopulated asynchronously by getProfile. The cohort-scoped Weekly plan only appears when
  // memberId is sent, so we must re-fetch once it arrives rather than only on mount.
  useEffect(() => {
    handle.getStripePlan()
  }, [credentials?.memberId])

  useEffect(() => {
    // Everyone opens on Yearly, subscribers on a monthly plan included: it is the better price,
    // and their own plan is one tap away. (paymentProfile.interval can't be trusted for this
    // anyway: login reports 'month' for anyone without a subscription.)
    //
    // The exceptions are arrivals whose promised price exists only on monthly, where Yearly
    // would hide it: drip landings (&until= from /claim/offer, &flow=trial-offer from
    // /claim/trial-offer, both with monthly-scoped coupons) and a live monthly-only campaign
    // (Dragon Boat, a Back-to-School invite, the win-back, whose Meta ad lands here).
    if (urlUntilRef.current > 0 || urlFlowRef.current === 'trial-offer' || isMonthlyPromoActive) {
      setBilling('monthly')
      return
    }
    setBilling('yearly')
  }, [isMonthlyPromoActive])

  const handle = {
    getStripePlan: async () => {
      await getStripePlan(credentials?.memberId).then((res: any) => {
        const mainPlan = {
          monthly: res?.monthly?.length > 0 ? handle.sortPlans([...res?.monthly]) : [],
          yearly: res?.yearly?.length > 0 ? handle.sortPlans([...res?.yearly]) : [],
        }
        setStripePlan(mainPlan)
        setRegionalPricing(res?.pricing || null)
        // U9: cohort member saw the Weekly offer on the plan page.
        if (mainPlan.yearly.some((p: any) => p.value === membershipNameType.Weekly)) {
          captureWeeklyEvent('weekly_offer_plan_viewed')
        }
      })
    },
    sortPlans: (plans: any[]) => {
      const planOrder = [membershipNameType.Pro, membershipNameType.Standard, membershipNameType.Weekly, membershipNameType.Free]
      return plans.sort((a, b) => {
        const aIndex = planOrder.indexOf(a.value)
        const bIndex = planOrder.indexOf(b.value)
        return aIndex - bIndex
      })
    },
    getButtonText: ({ name, value, periodType }: { name: string; value: string; periodType: string }) => {
      const currentPlanName = membership?.name

      if (
        (currentPlanName === value && paymentProfile?.interval === periodType) ||
        (currentPlanName === value && currentPlanName === membershipNameType.Free) ||
        (currentPlanName === value && currentPlanName === membershipNameType.Weekly)
      ) {
        return `You're On ${name.replace(' Plan', '')}`
      }

      switch (currentPlanName) {
        case membershipNameType.Free:
          return 'Upgrade'
        case membershipNameType.Standard:
          if (value === membershipNameType.Pro) return 'Upgrade'
          if (value === membershipNameType.Standard && periodType === 'yearly') return 'Upgrade'
          if (value === membershipNameType.Standard && periodType === 'monthly') return 'Downgrade'
          if (value === membershipNameType.Weekly) return 'Downgrade'
          if (value === membershipNameType.Free) return 'Downgrade'

          break
        case membershipNameType.Weekly:
          // Weekly is the entry paid tier: Standard/Pro are upgrades, Free is a downgrade.
          if (value === membershipNameType.Standard || value === membershipNameType.Pro) return 'Upgrade'
          if (value === membershipNameType.Free) return 'Downgrade'
          break
        case membershipNameType.Pro:
          if (value === membershipNameType.Pro && periodType === 'yearly') return 'Upgrade'
          if (value === membershipNameType.Pro && periodType === 'monthly') return 'Downgrade'
          if (value === membershipNameType.Standard || value === membershipNameType.Weekly || value === membershipNameType.Free)
            return 'Downgrade'
          break
        default:
          return ''
      }
      return ''
    },

    getYearlyDiscounts: useCallback(() => {
      if (!stripePlan || !stripePlan?.monthly || !stripePlan?.yearly) return 0

      const discounts = stripePlan?.monthly
        ?.map((monthlyPlan: any) => {
          const monthlyPrice = getPlanAmount(monthlyPlan)
          const yearlyPlan = stripePlan?.yearly?.find((y: any) => y?.value === monthlyPlan?.value)
          if (!yearlyPlan) return null
          const yearlyPrice = getPlanAmount(yearlyPlan)
          if (!monthlyPrice || !yearlyPrice) return null
          const yearlyDividedBy12 = yearlyPrice / 12
          const discount = ((monthlyPrice - yearlyDividedBy12) / monthlyPrice) * 100
          return discount
        })
        ?.filter((d: any) => d !== null && !isNaN(d))

      if (!discounts?.length) return 0
      const averageDiscount = discounts?.reduce((sum: number, d: number) => sum + d, 0) / discounts.length
      return Math.ceil(averageDiscount)
    }, [stripePlan]),
    getCurrentPlan: (value, priceId) => {
      const isCurrentPlan =
        (membership?.name === value && membership?.name === membershipNameType.Free) ||
        paymentProfile?.priceId === priceId
      return isCurrentPlan
    },
    downloadInvoice: async () => {
      await downloadInvoice().then((res: any) => {
        if (res?.invoice_pdf) {
          window.open(res?.invoice_pdf, '_blank')
        }
      })
    },
    managePaymentMethod: async () => {
      if (stripe?.customerID) {
        await stripeUpdatePaymentMethodLink().then((res: any) => {
          if (res?.url) {
            window.open(res.url, '_self')
          }
        })
      }
    },
    // codeOverride lets the URL-prefill flow (/setting?coupon=CODE#plan) validate a code in the
    // same render it was set — state updates wouldn't be visible to this closure yet.
    applyCoupon: async (codeOverride?: string) => {
      const code = (codeOverride ?? couponCode).trim()
      if (!code) {
        setCouponError(t('coupon.enterCode'))
        return
      }

      setCouponError('')
      setIsValidatingCoupon(true)

      const currentPlans = stripePlan[billing]
      if (!currentPlans || currentPlans.length === 0) {
        setCouponError(t('coupon.unableToValidate'))
        setIsValidatingCoupon(false)
        return
      }

      // Any paid price works here — the endpoint no longer rejects on product scope, it
      // returns applies_to_products and each card decides whether the discount applies.
      const priceId = currentPlans.find((p: any) => p.priceId)?.priceId || ''

      try {
        const result = await validateCouponCode({ code, priceId })
        if (result && typeof result === 'object' && 'id' in result) {
          const details = result as CouponDetails
          const appliesTo = details.applies_to_products || []
          const knownProductIds = [...(stripePlan?.monthly || []), ...(stripePlan?.yearly || [])]
            .map((p: any) => p.productId)
            .filter(Boolean)
          // Product-restricted coupon that matches none of the plans we sell here (only
          // decidable when the plans payload carries productIds).
          if (appliesTo.length > 0 && knownProductIds.length > 0 && !knownProductIds.some((id) => appliesTo.includes(id))) {
            setCouponError(t('coupon.notValidForPlans'))
            setCouponDetails(null)
            return
          }
          setCouponDetails(details)
          setCouponError('')
        } else {
          setCouponError(typeof result === 'string' ? result : t('coupon.invalid'))
          setCouponDetails(null)
        }
      } catch (error) {
        setCouponError(t('coupon.invalid'))
        setCouponDetails(null)
      } finally {
        setIsValidatingCoupon(false)
      }
    },
    removeCoupon: () => {
      setCouponDetails(null)
      setCouponCode('')
      setCouponError('')
    },
    upgradePlan: async (detail: any) => {
      if (pendingPriceId) return
      setCheckoutError(null)
      setPendingPriceId(detail?.priceId || '')
      if (detail?.value === membershipNameType.Weekly) {
        captureWeeklyEvent('weekly_checkout_started', { priceId: detail?.priceId })
      }
      const affiliateId =
        typeof window !== 'undefined' && (window as any).affiliateId
          ? String((window as any).affiliateId)
          : undefined

      try {
        const res: any = await stripeCheckoutLink({
          priceId: detail?.priceId,
          ...(detail?.currency && detail.currency !== 'USD' && { currency: detail.currency }),
          ...(detail.appliedCouponCode && { couponCode: detail.appliedCouponCode }),
          ...(affiliateId && { affiliateId }),
          ...(detail?.trialDays && { trialDays: detail.trialDays }),
        })
        const url = res?.return_url || res?.url
        if (url) {
          // Leaving for Stripe: the button keeps saying "Opening checkout…" until the page goes.
          window.open(url, '_self')
          return
        }
      } catch (error) {
        // falls through to the inline error below
      }
      // No checkout URL came back. Say so under the button rather than leaving it silently dead.
      setCheckoutError({ priceId: detail?.priceId || '', message: t('checkoutError') })
      setPendingPriceId(null)
    },
    downgradePlan: async (detail: any) => {
      try {
        await downGradePlan({
          customer: stripe?.customerID,
          plan_name: detail.value,
        }).then((res) => {
          if (!res) {
            getProfile()
            window.dataLayer.push({
              event: 'Plan Downgrade',
              action: 'Already Downgraded',
              planName: detail.value,
              customerId: stripe?.customerID,
              memberId: credentials?.memberId,
              page: {
                url: window.location.href,
                title: 'maayot \u2022 Your daily Chinese lesson',
              },
            })
          } else {
            window.dataLayer.push({
              event: 'Plan Downgrade Initiated',
              action: 'Redirect to Upgrade',
              planName: detail.value,
              customerId: stripe?.customerID,
              memberId: credentials?.memberId,
              page: {
                url: window.location.href,
                title: 'maayot \u2022 Your daily Chinese lesson',
              },
            })
            handle.upgradePlan(detail)
          }
        })
      } catch (error) {
        window.dataLayer.push({
          event: 'Plan Downgrade Failed',
          action: 'Error',
          planName: detail.value,
          customerId: stripe?.customerID,
          memberId: credentials?.memberId,
          error: error instanceof Error ? error.message : 'Unknown error',
          page: {
            url: window.location.href,
            title: 'maayot \u2022 Your daily Chinese lesson',
          },
        })
      }
    },
    resumePlan: async () => {
      setIsResuming(true)
      try {
        await churnResume()
        await getProfile()
        captureChurnEvent('churn_plan_resumed', { plan: membership?.name })
      } catch (error) {
        // profile refetch on next load will reconcile
      } finally {
        setIsResuming(false)
      }
    },
  }

  // Coupon prefill from the URL (/setting?coupon=CODE&until=UNIX&flow=trial-offer#plan — how the
  // /claim/* routes land here): once the plans are loaded, drop the code into the voucher input
  // and run the exact same validation as manual entry so the discounted prices show without any
  // typing. One-shot; manual entry stays untouched afterwards. `until` (unix seconds) is the
  // offer deadline — when it's in the future and the code validated, a live countdown renders
  // above the plans. `flow=trial-offer` (Track A Day 3) additionally renders the plan → 5 free
  // days → 50%-off journey explainer and puts 5 trial days on the monthly Standard/Pro checkouts.
  const urlCouponRef = useRef<string | null>(null)
  const urlUntilRef = useRef<number>(0)
  const urlFlowRef = useRef<string>('')
  const urlTrialRef = useRef<number>(-1)
  if (urlCouponRef.current === null) {
    const params = new URLSearchParams(window.location.search)
    urlCouponRef.current = params.get('coupon') || ''
    const until = Number(params.get('until'))
    urlUntilRef.current = isFinite(until) && until > 0 ? Math.floor(until) : 0
    urlFlowRef.current = params.get('flow') || ''
    const trialParam = Number(params.get('trial'))
    urlTrialRef.current = isFinite(trialParam) && params.get('trial') !== null ? trialParam : -1
  }
  const isTrialOfferFlow = urlFlowRef.current === 'trial-offer'
  // Server-decided entitlement carried by /claim/trial-offer: 0 means the person
  // has trialled before (day pass included) — 50% only, no second free days.
  // Absent (-1) defaults to including the trial, matching the old behavior.
  const trialOfferIncludesTrial = urlTrialRef.current !== 0
  const urlCouponApplied = useRef(false)
  useEffect(() => {
    const urlCoupon = (urlCouponRef.current || '').trim()
    if (!urlCoupon || urlCouponApplied.current) return
    if (!stripePlan?.[billing]?.length) return // applyCoupon needs a live priceId to validate against
    urlCouponApplied.current = true
    const code = urlCoupon.toUpperCase()
    // A deadline in the URL means the drip-offer flow, whose coupon is
    // monthly-scoped — land on the monthly tab so the discount is visible
    // immediately instead of a yearly view with a "monthly only" note.
    if (urlUntilRef.current > 0) setBilling('monthly')
    setShowCouponInput(true)
    setCouponCode(code)
    handle.applyCoupon(code)
  }, [stripePlan, billing])

  // Live Standard list prices, passed to the churn flow so its switch offers quote real
  // (regionally priced) numbers instead of hardcoded USD.
  const churnStandardPrices = useMemo(() => {
    const monthlyPlan = (stripePlan?.monthly || []).find((p: any) => p.value === membershipNameType.Standard)
    const yearlyPlan = (stripePlan?.yearly || []).find((p: any) => p.value === membershipNameType.Standard)
    return {
      monthly: monthlyPlan ? getPlanAmount(monthlyPlan) : null,
      yearly: yearlyPlan ? getPlanAmount(yearlyPlan) : null,
      currency: yearlyPlan?.currency || monthlyPlan?.currency || 'USD',
    }
  }, [stripePlan?.monthly, stripePlan?.yearly])

  // Names of the plans a product-restricted voucher discounts, for the applied-voucher note.
  // Empty when the voucher is unrestricted (applies everywhere) — no note needed then.
  const couponScopePlanNames = useMemo(() => {
    const scope = couponDetails?.applies_to_products || []
    if (scope.length === 0) return []
    const names = new Set<string>()
    ;[...(stripePlan?.monthly || []), ...(stripePlan?.yearly || [])].forEach((p: any) => {
      if (p.productId && scope.includes(p.productId)) {
        names.add(shortPlanName(p.name || p.value))
      }
    })
    return Array.from(names)
  }, [couponDetails, stripePlan])

  // Live deadline for the personal drip offer (/claim/offer lands here with &until=UNIX).
  // The countdown only renders while THAT code is the validated one — a manually entered
  // replacement code has no deadline attached. At zero the offer is over: the banner hides
  // itself and we drop the applied coupon so the cards fall back to list prices.
  const offerDeadline =
    couponDetails &&
    urlUntilRef.current > 0 &&
    (couponDetails.code || '').toUpperCase() === (urlCouponRef.current || '').trim().toUpperCase()
      ? urlUntilRef.current
      : 0
  // Trial-offer landings get ONE combined card (journey + deadline in its header),
  // so the standalone countdown banner only renders for plain /claim/offer landings.
  const offerCountdownBanner =
    !isTrialOfferFlow && offerDeadline > 0 && offerDeadline * 1000 > Date.now() ? (
      <OfferCountdown
        expiresAt={offerDeadline}
        label={
          couponDetails?.percent_off
            ? t('offer.percentExpiresIn', { percent: couponDetails.percent_off })
            : t('offer.expiresIn')
        }
        onExpire={() => handle.removeCoupon()}
      />
    ) : null

  // Trial-offer landings only (&flow=trial-offer): one card laying out the journey —
  // pick a plan, 5 free days, then the discount — with the offer deadline in its
  // header. Shown even when the offer claim failed (no coupon/deadline then) so the
  // page still explains what the email promised.
  const offerJourneyBanner = isTrialOfferFlow ? (
    <OfferJourney
      expiresAt={offerDeadline > 0 ? offerDeadline : undefined}
      onExpire={() => handle.removeCoupon()}
      {...(!trialOfferIncludesTrial && {
        title: t('offer.journey.title'),
        moments: [
          {
            when: t('offer.journey.todayWhen'),
            detail: t('offer.journey.todayDetail'),
            marker: 'now' as const,
            rail: 'offer' as const,
          },
          {
            when: t('offer.journey.laterWhen'),
            detail: t('offer.journey.laterDetail'),
            marker: 'later' as const,
          },
        ],
      })}
    />
  ) : null

  const proYearlyPriceId = useMemo(() => {
    const yearlyPlans = stripePlan?.yearly || []
    const proPlan = yearlyPlans.find((p: any) => p.value === membershipNameType.Pro)
    return proPlan?.priceId || ''
  }, [stripePlan])

  const updatePlanDetail = useMemo(() => {
    // Presence of a Weekly yearly plan means the API treated this user as a Weekly-test cohort
    // member (getPlans is cohort-scoped). For them, Standard is presented as the "Daily" plan.
    const isWeeklyCohort = (stripePlan?.yearly || []).some(
      (p: any) => p.value === membershipNameType.Weekly,
    )
    const plans =
      stripePlan[billing]?.map((planDetail) => {
        const displayName =
          isWeeklyCohort && planDetail?.value === membershipNameType.Standard
            ? t('dailyPlanName')
            : planDetail?.name
        const listPrice = getPlanAmount(planDetail)
        const planCurrency = planDetail?.currency || 'USD'
        const isYearlyBilling = billing === 'yearly'
        const basePrice = isYearlyBilling ? listPrice / 12 : listPrice
        const isCurrentPlan = handle.getCurrentPlan(planDetail?.value, planDetail?.priceId)
        // Product-restricted coupons (Stripe "Applies to") only discount matching plans.
        // An unrestricted coupon (empty applies_to_products) discounts every paid plan.
        const couponProductScope = couponDetails?.applies_to_products || []
        // Interval-restricted coupons (interval_scope 'month', e.g. the drip offer) only
        // discount monthly prices — checkout rejects them on yearly, so never preview a
        // yearly price we can't honor. Same idea as the product scoping above.
        const couponMatchesInterval = couponDetails?.interval_scope !== 'month' || billing === 'monthly'
        const couponAppliesToPlan = Boolean(
          couponDetails &&
            couponMatchesInterval &&
            (couponProductScope.length === 0 ||
              (planDetail?.productId && couponProductScope.includes(planDetail.productId))),
        )
        // Campaign promos define absolute USD amounts, so they only combine with USD
        // pricing; regionally-priced visitors keep their (already discounted) local rates.
        const applyAprilPromo =
          planCurrency === 'USD' &&
          shouldApplyAprilStandardPromoToPlan({
            planValue: planDetail?.value,
            billing,
            isCurrentPlan,
            hasManualCoupon: couponAppliesToPlan,
          })
        const applyWinBackPromo = shouldApplyWinBackPromoToPlan({
          planValue: planDetail?.value,
          billing,
          isCurrentPlan,
          hasManualCoupon: couponAppliesToPlan,
          membershipName: membership?.name,
        })
        const applyDragonBoatPromo = shouldApplyDragonBoatPromoToPro({
          planValue: planDetail?.value,
          billing,
          isCurrentPlan,
          hasManualCoupon: couponAppliesToPlan,
          membershipName: membership?.name,
        })
        // Back-to-School takes precedence over Dragon Boat: it is invite-only and therefore the
        // more specific offer. The two windows don't overlap today, but ordering it explicitly
        // means a date change can't silently hand an invited learner the weaker deal.
        const applyBackToSchool = shouldApplyBackToSchool2026ToPlan({
          planValue: planDetail?.value,
          billing,
          isCurrentPlan,
          hasManualCoupon: couponAppliesToPlan,
          isAllowListed: backToSchool2026?.isEligible,
          membershipName: membership?.name,
        })
        let displayPrice = basePrice
        let originalPrice: number | undefined
        let billingChargeAmount = isYearlyBilling ? listPrice : undefined
        let originalBillingChargeAmount: number | undefined
        // Only hand the coupon to checkout for plans it actually discounts — checkout
        // re-validates and would silently drop it, showing a price we never promised.
        let appliedCouponCode = couponAppliesToPlan ? couponDetails?.code : undefined
        let isAprilPromo = false
        let isDragonBoatPromo = false
        let isBackToSchoolPromo = false
        let isWinBackPromo = false

        // amount_off coupons are denominated in a single currency (USD here) — only
        // apply that math when the displayed prices are in the same currency.
        // percent_off is currency-agnostic and always applies.
        // The backend already converts amount_off from Stripe cents to dollars.
        const amountOff =
          couponDetails?.amount_off &&
          (couponDetails?.currency || 'usd').toUpperCase() === planCurrency
            ? couponDetails.amount_off
            : undefined
        if (couponAppliesToPlan) {
          originalPrice = basePrice
          if (isYearlyBilling) {
            originalBillingChargeAmount = listPrice
            if (couponDetails?.percent_off) {
              billingChargeAmount = Math.max(0, listPrice * (1 - couponDetails.percent_off / 100))
            } else if (amountOff) {
              billingChargeAmount = Math.max(0, listPrice - amountOff)
            }
            displayPrice = (billingChargeAmount ?? 0) / 12
          } else if (couponDetails?.percent_off) {
            displayPrice = Math.max(0, basePrice * (1 - couponDetails.percent_off / 100))
          } else if (amountOff) {
            displayPrice = Math.max(0, basePrice - amountOff)
          }
        } else if (applyAprilPromo) {
          displayPrice = APRIL_STANDARD_PROMO.promoMonthlyEquivalent
          originalPrice = APRIL_STANDARD_PROMO.regularMonthlyPrice
          billingChargeAmount = APRIL_STANDARD_PROMO.promoYearlyPrice
          originalBillingChargeAmount = APRIL_STANDARD_PROMO.regularYearlyPrice
          appliedCouponCode = APRIL_STANDARD_PROMO.couponCode
          isAprilPromo = true
        } else if (applyBackToSchool) {
          // 50% off monthly Standard or Pro for 3 months. basePrice is the live monthly price,
          // so this stays correct under regional pricing (percent_off is currency-agnostic).
          const pricing = getBackToSchool2026Pricing(basePrice)
          if (pricing) {
            displayPrice = pricing.promoMonthly
            originalPrice = pricing.regularMonthly
            appliedCouponCode = BACK_TO_SCHOOL_2026.couponCode
            isBackToSchoolPromo = true
          }
        } else if (applyDragonBoatPromo) {
          // Pro monthly at 50% off for 3 months. basePrice is the live monthly Pro price.
          const pricing = getDragonBoatPromoPricing(basePrice)
          if (pricing) {
            displayPrice = pricing.promoMonthly
            originalPrice = pricing.regularMonthly
            appliedCouponCode = DRAGON_BOAT_PROMO.couponCode
            isDragonBoatPromo = true
          }
        } else if (applyWinBackPromo) {
          // Standard or Pro, monthly, half price for the first three months. basePrice is
          // whichever plan's live monthly price, so the same branch serves both cards.
          //
          // Last in the chain on purpose: it is the broadest offer of the four, aimed at
          // everyone who is not paying. An invited Back-to-School learner keeps the offer
          // that was mailed to them, and a manual coupon still beats all of them.
          const pricing = getWinBackPromoPricing(basePrice)
          if (pricing) {
            displayPrice = pricing.promoMonthly
            originalPrice = pricing.regularMonthly
            appliedCouponCode = WIN_BACK_PROMO.couponCode
            isWinBackPromo = true
          }
        }

        return {
          ...planDetail,
          name: displayName,
          periodType: billing,
          currency: planCurrency,
          price: displayPrice,
          originalPrice,
          billingChargeAmount,
          originalBillingChargeAmount,
          isCurrentPlan,
          isAprilPromo,
          isDragonBoatPromo,
          isBackToSchoolPromo,
          isWinBackPromo,
          buttonText: handle.getButtonText({ name: displayName, value: planDetail.value, periodType: billing }),
          appliedCouponCode,
          // The Dragon Boat and win-back promos replace the free trial while they're live, so
          // each card shows one clear offer rather than two competing ones.
          //
          // A promo that doesn't replace the trial stacks with it into one offer ("5 days free,
          // then 50% off for 3 months"). Stripe applies trial_period_days and the promotion code
          // to the same subscription, and a trial shorter than a month keeps all three post-trial
          // monthly invoices inside the coupon's 3-month window, so the learner really does get
          // three discounted months after the trial. 5 days matches every other Free-learner
          // trial (onboarding, /stories, the end-of-story screen).
          ...(isTrialEligible &&
          !isDragonBoatPromo &&
          !isWinBackPromo &&
          (planDetail?.value === membershipNameType.Pro || planDetail?.value === membershipNameType.Standard)
            ? { trialEligible: true, trialDays }
            : {}),
          // Trial-offer landing (&flow=trial-offer, Track A Day 3): checkout must carry the
          // 5 free days ahead of the 50%-off coupon. Monthly only (the drip coupon's interval
          // scope) and only for the upgradeable paid tiers. This overrides the generic Pro
          // trial above; trialEligible is forced off so the hero's own trial CTA copy can't
          // show, and the journey banner carries the trial message instead.
          ...(isTrialOfferFlow &&
          billing === 'monthly' &&
          !isCurrentPlan &&
          (planDetail?.value === membershipNameType.Standard || planDetail?.value === membershipNameType.Pro)
            ? // buttonLabel is DISPLAY ONLY: buttonText ('Upgrade') stays the behavioral
              // discriminator for click routing and wallet-row visibility. The 5 free
              // days only attach for people with no trial history (server re-checks).
              (trialOfferIncludesTrial
                ? { trialEligible: false, trialDays: 5, buttonLabel: t('offer.claimTrial') }
                : { trialEligible: false, buttonLabel: t('offer.claimHalfOff') })
            : {}),
        }
      }) || []

    return plans
  }, [
    stripePlan[billing],
    stripePlan?.yearly,
    membership?.name,
    billing,
    couponDetails,
    isTrialEligible,
    trialDays,
    isDragonBoatActive,
    isWinBackActive,
    backToSchool2026?.isEligible,
    paymentProfile?.interval,
  ])

  const proPlan = updatePlanDetail.find((p: any) => p.value === membershipNameType.Pro)
  const standardPlan = updatePlanDetail.find((p: any) => p.value === membershipNameType.Standard)

  // Weekly is annual-only, so it always lives in the yearly list. We build its card from there
  // regardless of the active billing tab so it can render active (yearly) or locked (monthly).
  const weeklyPlan = useMemo(() => {
    const weeklyYearly = (stripePlan?.yearly || []).find((p: any) => p.value === membershipNameType.Weekly)
    if (!weeklyYearly) return undefined
    const yearlyPrice = getPlanAmount(weeklyYearly)
    return {
      ...weeklyYearly,
      periodType: 'yearly',
      currency: weeklyYearly?.currency || 'USD',
      price: yearlyPrice / 12, // highlighted monthly-equivalent
      billingChargeAmount: yearlyPrice, // charged yearly
      isCurrentPlan: handle.getCurrentPlan(weeklyYearly?.value, weeklyYearly?.priceId),
      buttonText: handle.getButtonText({
        name: weeklyYearly?.name,
        value: weeklyYearly?.value,
        periodType: 'yearly',
      }),
    }
  }, [stripePlan?.yearly, membership?.name, paymentProfile?.interval, paymentProfile?.priceId])

  const isWeeklyLocked = Boolean(weeklyPlan) && billing === 'monthly'
  const shouldPromoteStandardYearly = billing === 'yearly' && Boolean(standardPlan?.isAprilPromo)
  const isOnFreePlan = membership?.name === membershipNameType.Free
  const isOnPaidPlan =
    membership?.name === membershipNameType.Standard ||
    membership?.name === membershipNameType.Pro ||
    membership?.name === membershipNameType.Weekly
  const offersTrial = updatePlanDetail.some((p: any) => p.trialEligible)

  // The campaign treatment for one card. Back-to-School runs without a clock: its invitation
  // block above the plans carries the deadline.
  const promoFor = (plan: any): PlanPromo | undefined => {
    if (plan?.isDragonBoatPromo) return { ...DRAGON_BOAT_PROMO, getTimeLeft: getDragonBoatPromoTimeLeft }
    if (plan?.isBackToSchoolPromo) return BACK_TO_SCHOOL_2026
    if (plan?.isWinBackPromo) return { ...WIN_BACK_PROMO, getTimeLeft: getWinBackPromoTimeLeft }
    if (plan?.isAprilPromo) return { getTimeLeft: getAprilStandardPromoTimeLeft }
    return undefined
  }

  const cardProps = (plan: any) => ({
    plan,
    tagline: TAGLINE_KEYS[plan.value] ? t(TAGLINE_KEYS[plan.value]) : '',
    promo: promoFor(plan),
    currentPlanValue: membership?.name,
    pendingPriceId,
    error: checkoutError && checkoutError.priceId === plan.priceId ? checkoutError.message : undefined,
    onUpgrade: handle.upgradePlan,
    onDowngrade: handle.downgradePlan,
    onWalletStart: (p: any) => {
      if (p.value === membershipNameType.Weekly) {
        captureWeeklyEvent('weekly_checkout_started', { priceId: p.priceId, method: 'wallet' })
      }
    },
  })

  // Weekly-test cohort members see the entry tiers first; the April Standard promo (yearly)
  // led with Standard while it ran. Everyone else: Pro, then Standard. The comparison table
  // uses the same order, so each column sits under its card.
  const orderedPlans = (
    weeklyPlan
      ? [weeklyPlan, standardPlan, proPlan]
      : shouldPromoteStandardYearly
        ? [standardPlan, proPlan]
        : [proPlan, standardPlan]
  ).filter(Boolean)

  const planCards = orderedPlans.map((plan: any) => (
    <PlanOption
      key={plan.value}
      {...cardProps(plan)}
      featured={plan.value === membershipNameType.Pro}
      locked={
        plan.value === membershipNameType.Weekly && isWeeklyLocked
          ? { note: t('weeklyLocked.note'), actionLabel: t('weeklyLocked.action'), onUnlock: () => setBilling('yearly') }
          : undefined
      }
    />
  ))

  const comparePlans: ComparePlan[] = orderedPlans.map((plan: any) => ({
    key: shortPlanName(plan.value).toLowerCase() as ComparePlanKey,
    name: shortPlanName(plan.name),
    features: plan.features,
  }))
  const planCount = Math.max(orderedPlans.length, 2)

  // Only a real subscription has a billing period: login reports 'month' for everyone else,
  // comped plans included.
  const billedInterval = !paymentProfile?.priceId
    ? ''
    : paymentProfile?.interval === 'year' || paymentProfile?.interval === 'yearly'
      ? 'yearly'
      : paymentProfile?.interval === 'month' || paymentProfile?.interval === 'monthly'
        ? 'monthly'
        : ''
  const currentPlanName = shortPlanName(membership?.name)

  // Where the learner stands, before anything is offered to them.
  const planStatus = isOnFreePlan ? (
    <>{t('status.free')}</>
  ) : paymentProfile?.cancelAtPeriodEnd ? (
    <>
      {t('status.endsOn', { plan: currentPlanName, date: validDate })}{' '}
      <button className='plan-status-action' onClick={handle.resumePlan} disabled={isResuming}>
        {isResuming ? t('status.resuming') : t('status.resumePlan')}
      </button>
    </>
  ) : paymentProfile?.pausedUntil ? (
    <>
      {t('status.paused', { date: formatDate(paymentProfile.pausedUntil * 1000, 'medium') })}{' '}
      <button className='plan-status-action' onClick={handle.resumePlan} disabled={isResuming}>
        {isResuming ? t('status.resuming') : t('status.resumeNow')}
      </button>
    </>
  ) : paymentProfile?.isDayPass && paymentProfile?.expirationUnix ? (
    <>{t('status.dayPass', { plan: currentPlanName, date: validDate })}</>
  ) : membership?.name ? (
    <>
      {billedInterval === 'yearly'
        ? t('status.currentYearly', { plan: currentPlanName })
        : billedInterval === 'monthly'
          ? t('status.currentMonthly', { plan: currentPlanName })
          : t('status.current', { plan: currentPlanName })}
    </>
  ) : null

  const plansTitle = offersTrial
    ? t('plansTitle.trial', {
        plans: formatList(
          updatePlanDetail
            .filter((p: any) => p.trialEligible)
            .map((p: any) => shortPlanName(p.name))
            .reverse(),
          { type: 'disjunction' },
        ),
        days: trialDays,
      })
    : isOnPaidPlan
      ? t('plansTitle.change')
      : t('plansTitle.choose')

  return (
    <div className='plan-page'>
      <div className='settings-plan-header'>
        <h4 className={'m-h4 m-gray-1'}>{t('title')}</h4>
        {stripe?.customerID && (
          <button className='plan-text-link' onClick={() => handle.managePaymentMethod()}>
            {t('managePaymentMethod')}
          </button>
        )}
      </div>

      {planStatus && (
        <p className='plan-status'>
          {planStatus}
          {/* Invoices live in Stripe: a comped or app-store plan has none to download. */}
          {!isOnFreePlan && membership?.name && stripe?.customerID && !paymentProfile?.iapManaged && (
            <>
              {' '}
              <button className='plan-status-action' onClick={() => handle.downloadInvoice()}>
                {t('status.downloadInvoice')}
              </button>
            </>
          )}
        </p>
      )}

      {/* Give 2 weeks, get 2 weeks. Renders only for paying Stripe subscribers. */}
      <InviteFriendsCard placement='settings_plan' />

      {/* Back-to-School 2026 — invite-only, so it renders off the server's allow-list. Placed
          above the plan cards it explains; the discounted prices appear on the cards themselves. */}
      {isBackToSchoolEligible && <BackToSchoolInvite showTrialTerm={isTrialEligible} />}

      {/* CNY campaign copy quotes fixed USD amounts — shown only to USD-priced visitors */}
      {showCNYPromo && proYearlyPriceId && (!regionalPricing || regionalPricing.currency === 'USD') && (
        <CNYPromoSection proYearlyPriceId={proYearlyPriceId} />
      )}

      {offerJourneyBanner}

      {offerCountdownBanner}

      <section
        className='plan-choose'
        aria-labelledby='plan-choose-title'
        style={{ '--plan-count': planCount } as React.CSSProperties}
      >
        <div className='plan-grid'>
          <div className='plan-intro'>
            <h2 className='plan-section-title' id='plan-choose-title'>
              {plansTitle}
            </h2>
            {proPlan && standardPlan && (
              <p className='plan-intro-lead'>{t('intro')}</p>
            )}
            <div className='plan-billing' role='group' aria-label={t('billing.label')}>
              {billingPlan.map((item) => (
                <button
                  key={item.value}
                  type='button'
                  data-billing={item.value}
                  aria-pressed={billing === item.value}
                  onClick={() => setBilling(item.value)}
                  className={`plan-billing-btn${billing === item.value ? ' active' : ''}`}
                >
                  {t(item.labelKey)}
                  {item.value === 'yearly' && handle.getYearlyDiscounts() > 0 && (
                    <span className='plan-billing-save'>
                      {t('billing.save', { percent: formatPercent(handle.getYearlyDiscounts() / 100) })}
                    </span>
                  )}
                </button>
              ))}
            </div>
            <RegionalPricingNotice pricing={regionalPricing} />
          </div>

          {updatePlanDetail?.length > 0 ? (
            planCards
          ) : (
            <>
              <div className='plan-option plan-option--skeleton' aria-busy='true' aria-label={t('loadingPlans')} />
              <div className='plan-option plan-option--skeleton' aria-hidden='true' />
            </>
          )}
        </div>

        {/* Voucher */}
        <div className='settings-account-coupon-section'>
          {!couponDetails ? (
            <>
              {!showCouponInput ? (
                <button className='plan-text-link settings-account-coupon-toggle' onClick={() => setShowCouponInput(true)}>
                  {t('coupon.toggle')}
                </button>
              ) : (
                <div className='settings-account-coupon-input-wrapper'>
                  <input
                    type='text'
                    className='settings-account-coupon-input'
                    placeholder={t('coupon.placeholder')}
                    aria-label={t('coupon.placeholder')}
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === 'Enter' && handle.applyCoupon()}
                  />
                  <button
                    className='settings-account-coupon-apply-btn'
                    onClick={() => handle.applyCoupon()}
                    disabled={isValidatingCoupon || !couponCode.trim()}
                  >
                    {isValidatingCoupon ? t('coupon.checking') : t('coupon.apply')}
                  </button>
                </div>
              )}
              {couponError && <div className='settings-account-coupon-error'>{couponError}</div>}
            </>
          ) : (
            <>
              <div className='settings-account-coupon-success'>
                <span className='settings-account-coupon-success-text'>
                  {t(couponScopePlanNames.length > 0 ? 'coupon.appliedScoped' : 'coupon.applied', {
                    code: couponDetails.code,
                    discount: couponDetails.percent_off
                      ? t('coupon.percentOff', { percent: couponDetails.percent_off })
                      : t('coupon.amountOff', {
                          amount: formatMoney(couponDetails?.amount_off ?? 0, couponDetails?.currency || 'USD'),
                        }),
                    plans: couponScopePlanNames.join(', '),
                  })}
                </span>
                <button className='plan-text-link settings-account-coupon-remove-btn' onClick={handle.removeCoupon}>
                  {t('coupon.remove')}
                </button>
              </div>
              {/* Interval-scoped code viewed on the wrong tab: prices above are undiscounted
                  (couponMatchesInterval), so quietly say why. */}
              {couponDetails.interval_scope === 'month' && billing === 'yearly' && (
                <div className='settings-account-coupon-interval-note'>{t('coupon.monthlyOnly')}</div>
              )}
            </>
          )}
        </div>

        {comparePlans.length > 0 && <PlanCompare plans={comparePlans} />}
      </section>

      {/* The teacher is what Pro adds, so anyone not already on Pro gets to see what that means. */}
      {proPlan && membership?.name !== membershipNameType.Pro && <ProDetails />}

      <PlanFaq
        trialDays={offersTrial ? trialDays : null}
        onAskDiscount={() => requestStudentDiscount(credentials?.email)}
      />

      {isOnPaidPlan && !paymentProfile?.cancelAtPeriodEnd && !paymentProfile?.pausedUntil && (
        <button
          className='plan-text-link plan-downgrade-free'
          // IAP-managed plans can only be cancelled from the mobile app's store
          // subscription — everyone else gets the regular churn flow.
          onClick={() => (paymentProfile?.iapManaged ? setOpenPaymentSupportModal(true) : setChurnFlowOpen(true))}
        >
          {t('downgradeToFree')}
        </button>
      )}

      <PaymentSupportModal
        openPaymentSupportModal={openPaymentSupportModal}
        setOpenPaymentSupportModal={setOpenPaymentSupportModal}
      />
      <ChurnFlowModal
        show={churnFlowOpen}
        onHide={() => setChurnFlowOpen(false)}
        membershipName={membership?.name}
        interval={paymentProfile?.interval || null}
        amount={paymentProfile?.subscriptionAmount ?? null}
        currency={paymentProfile?.subscriptionCurrency || 'USD'}
        saveOfferUsed={Boolean(paymentProfile?.saveOfferUsed)}
        repeatingDiscountActive={Boolean(paymentProfile?.repeatingDiscountActive)}
        // The free day pass is a trialing Stripe subscription with no card, so it satisfies
        // customerID + priceId. Treat it as "no subscription" here: every save offer resolves
        // to none, which is what the server enforces anyway.
        hasStripeSubscription={Boolean(
          stripe?.customerID && paymentProfile?.priceId && !paymentProfile?.isDayPass,
        )}
        expirationUnix={paymentProfile?.expirationUnix ?? null}
        standardMonthlyPrice={churnStandardPrices.monthly}
        standardYearlyPrice={churnStandardPrices.yearly}
        plansCurrency={churnStandardPrices.currency}
        onContactSupport={() => openSupportChat({ email: credentials?.email })}
      />
    </div>
  )
}

export default PlanTab
